import React, { useEffect, useMemo, useRef, useState } from "react";
import { displayAccountLabel } from "@common/helpers/accounting";
import { Account } from "@common/types/domains/accounting/account";
import { BaseCellProps } from "@components/spreadsheet/cells";
import { useShowAccountNumbers } from "@hooks/useShowAccountNumbers";
import { useAccounts } from "@hooks/useAccounts";
import { useLedgers } from "@api/ledger.api";
import { useWorkspaceAccrualAccounts } from "@api/account.api";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { AccountCombobox } from "@components/accounts/AccountCombobox";
import { BreakableText } from "@design-system/BreakableText/BreakableText";

interface Props extends BaseCellProps<string> {
    entityId?: number;
    ledgerBasis?: AccountingLedgerBasis;
    canClear?: boolean;
}

export const TransactionSplitLedgerCell: React.FC<Props> = ({
    value,
    isEditing,
    finishEditing,
    enterEditModeKey,
    entityId,
    ledgerBasis = AccountingLedgerBasis.cash,
    canClear = false,
}) => {
    const showAccountNumbers = useShowAccountNumbers();
    const [isOpen, setIsOpen] = useState(false);
    const selectionMadeRef = useRef(false);

    const { data: ledgers } = useLedgers(entityId);
    const hasLedger = useMemo(
        () => ledgers?.some((ledger) => ledger.basis === ledgerBasis) ?? false,
        [ledgers, ledgerBasis],
    );

    const { accounts } = useAccounts({
        entityId: hasLedger ? entityId : undefined,
        excludeLinkedToFinancialAccount: true,
        includeCurrentAccount: true,
        currentAccountId: value ?? undefined,
        ledgerBasis,
    });
    const workspaceAccrualAccounts = useWorkspaceAccrualAccounts();

    const selectedAccount = useMemo(() => {
        const fromEntity = accounts.find((account) => account.id === value);
        if (fromEntity) {
            return fromEntity;
        }
        if (ledgerBasis !== AccountingLedgerBasis.accruals) {
            return undefined;
        }
        return workspaceAccrualAccounts.data?.find(
            (account) => account.id === value,
        );
    }, [accounts, ledgerBasis, value, workspaceAccrualAccounts.data]);

    useEffect(() => {
        if (isEditing) {
            setIsOpen(true);
        }
    }, [isEditing]);

    if (!isEditing) {
        if (!selectedAccount) {
            return null;
        }
        return (
            <BreakableText
                text={displayAccountLabel(selectedAccount, showAccountNumbers)}
                showTooltip
            />
        );
    }

    if (!entityId || !hasLedger) {
        return null;
    }

    return (
        <div
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
                if (["Enter", "Escape"].includes(e.key)) {
                    finishEditing();
                }
            }}
        >
            <AccountCombobox
                selectedAccount={selectedAccount}
                onChange={(account: Account | null) => {
                    selectionMadeRef.current = true;
                    finishEditing(account?.id ?? null);
                }}
                entityId={entityId}
                showAccountCode={showAccountNumbers}
                addNewAccountButton={false}
                ledgerBasis={ledgerBasis}
                canClear={canClear}
                cellEditor={{ openingKey: enterEditModeKey }}
                open={isOpen}
                onOpenChange={(open) => {
                    setIsOpen(open);
                    if (!open && !selectionMadeRef.current) {
                        finishEditing();
                    }
                    selectionMadeRef.current = false;
                }}
            />
        </div>
    );
};
